import { useMemo } from 'react';
import { useUvsTimedOut, useTranslate } from '@shoflo/polymer';
import { faSignalStream } from '@fortawesome/pro-solid-svg-icons';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';

import useStreamActionUtils from '../useStreamActionUtils';
import { streamStarting, streamButtonStates } from './utils';
import useDelayedStreamButtonState from 'hooks/useDelayedStreamButtonState';

function useStreamButtonState({
  streamEndpoints,
  startOn,
  endOn,
  isStreamStopping
}) {
  const streamState = useDelayedStreamButtonState({
    streamEndpoints,
    startOn,
    endOn,
    isStreamStopping
  });

  const { translate } = useTranslate();

  const { onStartStreams, onStopStreams, onQuitStreams } =
    useStreamActionUtils();

  const isStarting = useMemo(
    () => streamStarting(streamEndpoints),
    [streamEndpoints]
  );

  const timedOut = useUvsTimedOut({
    uvsEntities: streamEndpoints,
    timeoutMs: 60000
  });

  if (timedOut) {
    const text = isStarting ? 'Cancel' : 'Force stop';

    return {
      text,
      icon: faSpinner,
      color: 'danger',
      tooltipText: '',
      onClickCallback: onQuitStreams,
      confirmOnClick: true,
      confirmOnClickQuery: `${text} live stream?`,
      disabled: false,
      opacity: 50
    };
  }

  const buttonStreamStatesProps = {
    [streamButtonStates.NOT_STARTED_NOT_PROVISIONED]: {
      icon: faSignalStream,
      color: 'primary',
      text: 'Start stream',
      tooltipText: translate('StreamButton_NoDestination_Popover'),
      onClickCallback: null,
      confirmOnClick: false,
      disabled: true,
      opacity: 50
    },
    [streamButtonStates.NOT_STARTED_DISABLED]: {
      icon: faSignalStream,
      color: 'primary',
      text: 'Start stream',
      tooltipText: translate('StreamButton_Time_Popover'),
      onClickCallback: null,
      confirmOnClick: false,
      disabled: true,
      opacity: 50
    },
    [streamButtonStates.NOT_STARTED_ENABLED]: {
      icon: faSignalStream,
      color: 'primary',
      text: 'Start stream',
      tooltipText: '',
      onClickCallback: onStartStreams,
      confirmOnClick: false,
      disabled: false,
      opacity: 100
    },
    [streamButtonStates.STARTING]: {
      icon: faSpinner,
      color: 'primary',
      text: 'Starting...',
      tooltipText: '',
      onClickCallback: null,
      confirmOnClick: false,
      disabled: true,
      opacity: 50
    },
    [streamButtonStates.STREAMING]: {
      icon: faSignalStream,
      color: 'danger',
      text: 'Stop streaming',
      tooltipText: '',
      onClickCallback: onStopStreams,
      confirmOnClick: true,
      confirmOnClickQuery: 'Stop live streaming?',
      disabled: false,
      opacity: 100
    },
    [streamButtonStates.STOPPING]: {
      icon: faSpinner,
      color: 'danger',
      text: 'Stopping...',
      tooltipText: '',
      onClickCallback: null,
      confirmOnClick: false,
      disabled: true,
      opacity: 50
    },
    [streamButtonStates.STOPPED_ENABLED]: {
      icon: faSignalStream,
      color: 'primary',
      text: 'Start stream',
      tooltipText: '',
      onClickCallback: onStartStreams,
      confirmOnClick: false,
      disabled: false,
      opacity: 100
    },
    [streamButtonStates.STOPPED_DISABLED]: {
      icon: faSignalStream,
      color: 'outline',
      text: 'Not streaming',
      tooltipText: translate('StreamButton_NotAvailable_Popover'),
      onClickCallback: null,
      confirmOnClick: false,
      disabled: true,
      opacity: 50
    }
  };

  return buttonStreamStatesProps[streamState];
}

export default useStreamButtonState;
